import { Controller } from "@hotwired/stimulus"

// Valida RUT y RUT Titular contra un endpoint server-side que retorna turbo_stream.
// El server actualiza el flash con admin_flash y responde:
//   - 200 + header X-Clp-Normalized-Rut → RUT válido, flipea flag a true y normaliza input
//   - 422 → RUT inválido, flag queda false
// Habilita el submit solo cuando ambos RUT están validados.
export default class extends Controller {
  static targets = [
    "rutInput", "validateButton", "progressBar",
    "accountRutInput", "accountRutButton", "accountProgressBar",
    "submitButton", "rutHint"
  ]

  static values = {
    validateRutUrl: String,
    validateAccountRutUrl: String,
    rutValidated: { type: Boolean, default: false },
    accountRutValidated: { type: Boolean, default: false },
    hintRut: { type: String, default: "Valida el RUT para habilitar el guardado" },
    hintAccountRut: { type: String, default: "Valida el RUT del Titular para habilitar el guardado" }
  }

  connect() {
    if (this.hasRutInputTarget && this.rutInputTarget.value.trim() !== "") {
      this.rutValidatedValue = true
    }
    if (this.hasAccountRutInputTarget && this.accountRutInputTarget.value.trim() !== "") {
      this.accountRutValidatedValue = true
    }
    this.updateSubmitState()
  }

  validateRut() {
    return this.#performValidation({
      url: this.validateRutUrlValue,
      input: this.rutInputTarget,
      button: this.hasValidateButtonTarget ? this.validateButtonTarget : null,
      progress: this.hasProgressBarTarget ? this.progressBarTarget : null,
      onResult: (ok) => { this.rutValidatedValue = ok }
    })
  }

  validateAccountRut() {
    return this.#performValidation({
      url: this.validateAccountRutUrlValue,
      input: this.accountRutInputTarget,
      button: this.hasAccountRutButtonTarget ? this.accountRutButtonTarget : null,
      progress: this.hasAccountProgressBarTarget ? this.accountProgressBarTarget : null,
      onResult: (ok) => { this.accountRutValidatedValue = ok }
    })
  }

  resetRut() {
    this.rutValidatedValue = false
    this.updateSubmitState()
  }

  resetAccountRut() {
    this.accountRutValidatedValue = false
    this.updateSubmitState()
  }

  updateSubmitState() {
    if (!this.hasSubmitButtonTarget) return

    const enabled = this.rutValidatedValue && this.accountRutValidatedValue
    this.submitButtonTarget.disabled = !enabled

    if (this.hasRutHintTarget) {
      if (!this.rutValidatedValue) {
        this.rutHintTarget.textContent = this.hintRutValue
        this.rutHintTarget.classList.remove("hidden")
      } else if (!this.accountRutValidatedValue) {
        this.rutHintTarget.textContent = this.hintAccountRutValue
        this.rutHintTarget.classList.remove("hidden")
      } else {
        this.rutHintTarget.textContent = ""
        this.rutHintTarget.classList.add("hidden")
      }
    }
  }

  async #performValidation({ url, input, button, progress, onResult }) {
    const value = input.value.trim()

    this.#toggle(progress, true)
    if (button) button.disabled = true
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true

    try {
      const formData = new FormData()
      formData.append("rut", value)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.#csrfToken
        },
        body: formData
      })

      const html = await response.text()
      if (html.trim()) Turbo.renderStreamMessage(html)

      const normalized = response.headers.get("X-Clp-Normalized-Rut")
      if (response.ok && normalized) input.value = normalized

      onResult(response.ok)
    } catch (_e) {
      onResult(false)
    } finally {
      if (button) button.disabled = false
      this.#toggle(progress, false)
      this.updateSubmitState()
    }
  }

  #toggle(target, visible) {
    if (!target) return
    target.classList.toggle("hidden", !visible)
  }

  get #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
